import { resolve } from "path";

type LoggerLike = Pick<Console, "log" | "warn" | "error">;

type RuntimeBufferLike = {
  drain(): Record<string, number>;
  readonly isEmpty: boolean;
};

type FetchPlatformInternal = (path: string, init: RequestInit, label: string) => Promise<Response>;

type WarmPoolProvisionerModule = {
  markAllRunningAppsForPlatformShutdown(): void;
  shutdownWarmPoolForPlatform(): Promise<void>;
};

type ShutdownMarker = {
  reason?: string;
  restartId?: string;
  at?: string;
} | null;

type RuntimeIdentity = {
  appName?: string;
  appDbId?: string | number;
};

function buildRuntimeIdentityHeaders(identity?: RuntimeIdentity): Record<string, string> {
  return {
    ...(identity?.appDbId ? { "X-Gencow-App-Id": String(identity.appDbId) } : {}),
    ...(identity?.appName ? { "X-Gencow-App-Name": identity.appName } : {}),
  };
}

export function buildUsageReportRequest(
  metrics: Record<string, number>,
  internalToken: string,
  identity?: RuntimeIdentity,
): RequestInit {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Internal-Token": internalToken,
      ...buildRuntimeIdentityHeaders(identity),
    },
    body: JSON.stringify({ metrics }),
  };
}

export async function flushRuntimeMetrics(params: {
  runtimeBuffer: RuntimeBufferLike;
  fetchPlatformInternal: FetchPlatformInternal;
  internalToken: string;
  label: string;
  appName?: string;
  appDbId?: string | number;
  logger?: LoggerLike;
}): Promise<boolean> {
  if (params.runtimeBuffer.isEmpty) {
    return false;
  }

  const logger = params.logger ?? console;
  const metrics = params.runtimeBuffer.drain();
  try {
    const response = await params.fetchPlatformInternal(
      "/internal/usage/report",
      buildUsageReportRequest(metrics, params.internalToken, {
        appName: params.appName,
        appDbId: params.appDbId,
      }),
      params.label,
    );
    if (!response.ok) {
      logger.warn(`[metering] flush failed: ${response.status}`);
    }
    return true;
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : String(error);
    logger.warn(`[metering] flush error:`, message);
    return true;
  }
}

export function startUsageMeteringFlush(params: {
  isBaas: boolean;
  meteringUrl?: string;
  runtimeBuffer: RuntimeBufferLike;
  fetchPlatformInternal: FetchPlatformInternal;
  internalToken: string;
  appName?: string;
  appDbId?: string | number;
  logger?: LoggerLike;
  setTimeoutImpl?: typeof setTimeout;
  setIntervalImpl?: typeof setInterval;
  randomImpl?: () => number;
  intervalMs?: number;
}): void {
  if (!params.isBaas) {
    return;
  }

  const logger = params.logger ?? console;
  const setTimeoutImpl = params.setTimeoutImpl ?? setTimeout;
  const setIntervalImpl = params.setIntervalImpl ?? setInterval;
  const randomImpl = params.randomImpl ?? Math.random;
  const intervalMs = params.intervalMs ?? 10_000;
  logger.log(`[metering] BaaS mode — flushing to ${params.meteringUrl} every ${intervalMs / 1000}s`);

  let flushing = false;
  const runFlush = () => {
    if (flushing) return;
    flushing = true;
    void flushRuntimeMetrics({
      runtimeBuffer: params.runtimeBuffer,
      fetchPlatformInternal: params.fetchPlatformInternal,
      internalToken: params.internalToken,
      appName: params.appName,
      appDbId: params.appDbId,
      label: "usage-report-flush",
      logger,
    }).finally(() => {
      flushing = false;
    });
  };

  const firstDelayMs = Math.floor(Math.max(0, Math.min(1, randomImpl())) * intervalMs);
  setTimeoutImpl(() => {
    runFlush();
    setIntervalImpl(runFlush, intervalMs);
  }, firstDelayMs);
}

export function buildShutdownLogLine(params: {
  signal: string;
  marker: ShutdownMarker;
  env: NodeJS.ProcessEnv;
}): string {
  const shutdownReason = params.marker?.reason || params.env.GENCOW_START_REASON || "unknown";
  const restartId = params.marker?.restartId || params.env.GENCOW_RESTART_ID || "unknown";
  const appName = params.env.GENCOW_APP_NAME || "unknown";
  const markerAt = params.marker?.at ? `, markerAt=${params.marker.at}` : "";

  return `\n[server] ${params.signal} received — shutting down... (app=${appName}, reason=${shutdownReason}, restartId=${restartId}${markerAt})`;
}

export async function shutdownWarmPoolIfPlatform(params: {
  isPlatform: boolean;
  functionsPath: string;
  logger?: LoggerLike;
  importProvisioner?: (path: string) => Promise<WarmPoolProvisionerModule>;
}): Promise<void> {
  if (!params.isPlatform) {
    return;
  }

  const logger = params.logger ?? console;
  const importProvisioner =
    params.importProvisioner ?? ((path: string) => import(path) as Promise<WarmPoolProvisionerModule>);

  try {
    const provisioner = await importProvisioner(resolve(params.functionsPath, "../src/provisioner.ts"));
    provisioner.markAllRunningAppsForPlatformShutdown();
    await provisioner.shutdownWarmPoolForPlatform();
  } catch (error: unknown) {
    logger.error(
      `[server] Failed to shutdown warm pool:`,
      error instanceof Error ? error.message : String(error),
    );
  }
}

export function createShutdownHandler(params: {
  isPlatform: boolean;
  isBaas: boolean;
  functionsPath: string;
  env: NodeJS.ProcessEnv;
  runtimeBuffer: RuntimeBufferLike;
  fetchPlatformInternal: FetchPlatformInternal;
  internalToken: string;
  readShutdownReasonFromMarker(): ShutdownMarker;
  close(): Promise<void>;
  logger?: LoggerLike;
  importProvisioner?: (path: string) => Promise<WarmPoolProvisionerModule>;
  exit?: (code: number) => never | void;
}): (signal: string) => Promise<void> {
  const logger = params.logger ?? console;
  const exit = params.exit ?? process.exit;

  return async (signal: string) => {
    const marker = params.readShutdownReasonFromMarker();
    logger.log(
      buildShutdownLogLine({
        signal,
        marker,
        env: params.env,
      }),
    );

    await shutdownWarmPoolIfPlatform({
      isPlatform: params.isPlatform,
      functionsPath: params.functionsPath,
      logger,
      importProvisioner: params.importProvisioner,
    });

    if (params.isBaas) {
      await flushRuntimeMetrics({
        runtimeBuffer: params.runtimeBuffer,
        fetchPlatformInternal: params.fetchPlatformInternal,
        internalToken: params.internalToken,
        appName: params.env.GENCOW_APP_NAME,
        appDbId: params.env.GENCOW_APP_DB_ID,
        label: "usage-report-shutdown",
        logger: {
          log: logger.log.bind(logger),
          error: logger.error.bind(logger),
          warn() {},
        },
      });
    }

    await params.close();
    exit(0);
  };
}

export function registerShutdownSignals(params: {
  processLike: {
    on(event: "SIGTERM" | "SIGINT", handler: () => void): void;
  };
  shutdown(signal: string): Promise<void>;
}): void {
  params.processLike.on("SIGTERM", () => {
    void params.shutdown("SIGTERM");
  });
  params.processLike.on("SIGINT", () => {
    void params.shutdown("SIGINT");
  });
}
